import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading } from "@/components/bi/Loading";
import { fmtBytes } from "@/components/bi/AdminUsage";
import { ArrowDown, ArrowRight, Eye, Database, HardDrive, Users, MoonStar, Activity } from "lucide-react";
import { cn } from "@/lib/utils";

const num = (n) => Number(n || 0).toLocaleString();
const COLS = [
  ["views", "Opens", Eye],
  ["queries", "Queries", Database],
  ["actions", "Actions", Activity],
  ["active_users", "Active", Users],
  ["storage_bytes", "Storage", HardDrive],
];

const relDays = (d) => (d === null || d === undefined ? "never" : d === 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`);

// Eazy fleet overview: every tenant ranked by what they actually use.
export function FleetOverview({ onEnter }) {
  const [days, setDays] = useState("30");
  const [sort, setSort] = useState("views");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get(`/usage/fleet?days=${days}`).then(({ data }) => setData(data)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Could not load the fleet");
      setData({ items: [], totals: {} });
    });
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const items = [...(data?.items || [])];
    items.sort((a, b) => (b[sort] || 0) - (a[sort] || 0) || a.tenant_name.localeCompare(b.tenant_name));
    return items;
  }, [data, sort]);

  if (!data) return <Loading label="Measuring every tenant" />;
  const t = data.totals || {};
  const max = Math.max(...rows.map((r) => r[sort] || 0), 1);

  return (
    <div className="space-y-4" data-testid="fleet-overview">
      <div className="glass-card p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-base md:text-lg text-[color:var(--ink)]">Fleet usage</h2>
          <p className="text-[11px] text-[color:var(--ink-3)] mt-0.5">
            {num(t.tenants)} tenants · {num(t.views)} dashboard opens · {num(t.queries)} queries
            (last {data.query_window_days}d) · {fmtBytes(t.storage_bytes)} stored
          </p>
        </div>
        <span data-testid="fleet-quiet-count" className={cn("text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5",
          t.quiet ? "bg-[color:rgba(255,176,32,0.14)] text-[color:var(--warning)]" : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)]")}>
          <MoonStar className="w-3.5 h-3.5" />{num(t.quiet)} quiet · nothing for {data.idle_after_days}+ days
        </span>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="fleet-days" className="h-8 w-[140px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>{[["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="grid grid-cols-[1.6fr_repeat(5,0.7fr)_auto] gap-2 px-4 py-2 border-b border-[color:var(--glass-border)] text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">
          <span>Tenant</span>
          {COLS.map(([key, label]) => (
            <button key={key} data-testid={`fleet-sort-${key}`} onClick={() => setSort(key)}
              className={cn("flex items-center gap-1 justify-end hover:text-[color:var(--ink)]",
                sort === key && "text-[color:var(--brand-ocean)] font-semibold")}>
              {label}{sort === key && <ArrowDown className="w-3 h-3" />}
            </button>
          ))}
          <span className="w-[86px] text-right">Last seen</span>
        </div>
        <div className="divide-y divide-[color:var(--glass-border)]" data-testid="fleet-rows">
          {rows.map((r) => (
            <div key={r.tenant_id} data-testid={`fleet-row-${r.tenant_id}`}
              className="grid grid-cols-[1.6fr_repeat(5,0.7fr)_auto] gap-2 px-4 py-2.5 items-center hover:bg-[color:var(--surface-2)] transition-colors">
              <span className="min-w-0">
                <button onClick={() => onEnter?.(r.tenant_id)}
                  className="text-xs text-[color:var(--ink)] hover:text-[color:var(--brand-ocean)] flex items-center gap-1.5 group">
                  <span className="truncate">{r.tenant_name}</span>
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
                <span className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-[color:var(--ink-3)]">{num(r.users)} people · {num(r.dashboards)} dashboards</span>
                  {r.quiet && <Badge variant="outline" data-testid={`fleet-quiet-${r.tenant_id}`} className="text-[9px] py-0 text-[color:var(--warning)] border-[color:var(--warning)]">quiet</Badge>}
                  {!r.allow_tenant_data_sources && <span className="text-[9px] text-[color:var(--ink-3)]">Eazy sources only</span>}
                </span>
                <div className="h-1 rounded-full bg-[color:var(--surface-2)] overflow-hidden mt-1 max-w-[220px]">
                  <div className="h-full rounded-full bg-[color:var(--brand-ocean)]" style={{ width: `${Math.max(2, ((r[sort] || 0) / max) * 100)}%` }} />
                </div>
              </span>
              {COLS.map(([key]) => (
                <span key={key} className={cn("text-xs text-right tabular-nums",
                  sort === key ? "text-[color:var(--ink)] font-semibold" : "text-[color:var(--ink-2)]")}>
                  {key === "storage_bytes" ? fmtBytes(r[key]) : num(r[key])}
                </span>
              ))}
              <span className="w-[86px] text-right text-[10px] text-[color:var(--ink-3)]">{relDays(r.idle_days)}</span>
            </div>
          ))}
          {!rows.length && <p className="px-4 py-8 text-center text-xs text-[color:var(--ink-3)]">No tenants yet.</p>}
        </div>
      </div>

      <p className="text-[10px] text-[color:var(--ink-3)] px-1">
        Query counts cover the last {data.query_window_days} days (that log rolls over); opens, actions and
        storage cover the selected window. Click a tenant to work inside it.
      </p>
      <div className="px-1">
        <Button size="sm" variant="outline" data-testid="fleet-refresh" onClick={load} className="bg-[color:var(--surface)]">Refresh</Button>
      </div>
    </div>
  );
}
